import * as THREE from 'three/webgpu';
import type { PointSelectionCandidate } from '../../reference/reference-point/ReferencePointController';
import { SketchEntitiesTransformSelectionRole } from '../../sketch/entities-transform';
import type { RawPointerEventPayload } from '../../viewer/moudles';
import type { EntitySelectionManager } from '../EntitySelectionManager';
import { AbstractSketchEntitiesTransformSelectionHandler } from './AbstractSketchEntitiesTransformSelectionHandler';

/**
 * 旋转实体命令选择处理器。
 */
export class SketchRotateEntitiesSelectionHandler extends AbstractSketchEntitiesTransformSelectionHandler {
	/** 场景旋转拖动抬起后是否需要抑制浏览器继续派发的一次 click。 */
	private suppressNextPointerAngleDragClick = false;

	/**
	 * @param entitySelectionManager 实体选择管理器。
	 */
	public constructor(entitySelectionManager: EntitySelectionManager) {
		super(entitySelectionManager);
	}

	/**
	 * 处理场景旋转鼠标按下。
	 *
	 * @param payload 原始鼠标事件。
	 */
	public override onMouseDown(payload: RawPointerEventPayload): void {
		if (payload.button === 0) {
			// 上一轮若因浏览器未合成 click 而残留标记，新一轮按下必须先恢复正常点击语义。
			this.suppressNextPointerAngleDragClick = false;
		}
		if (
			payload.button === 0
			&& this.entitySelectionManager.renderer.sketchRotateEntitiesController
				?.handlePointerAngleDragStart(payload.clientX, payload.clientY)
		) {
			this.consumePointerEvent(payload);
			return;
		}
		super.onMouseDown(payload);
	}

	/**
	 * 处理场景旋转鼠标拖动。
	 *
	 * @param payload 原始鼠标事件。
	 */
	public override onMouseMove(payload: RawPointerEventPayload): void {
		const controller = this.entitySelectionManager.renderer.sketchRotateEntitiesController;
		if (controller?.handlePointerAngleDragMove(payload.clientX, payload.clientY)) {
			this.consumePointerEvent(payload);
			return;
		}
		super.onMouseMove(payload);
	}

	/**
	 * 处理场景旋转鼠标抬起。
	 *
	 * @param payload 原始鼠标事件。
	 */
	public override onMouseUp(payload: RawPointerEventPayload): void {
		if (
			payload.button === 0
			&& this.entitySelectionManager.renderer.sketchRotateEntitiesController?.handlePointerAngleDragEnd()
		) {
			this.suppressNextPointerAngleDragClick = true;
			this.consumePointerEvent(payload);
			return;
		}
		super.onMouseUp(payload);
	}

	/**
	 * 处理视口离开或窗口失焦导致的旋转拖动取消。
	 *
	 * @param payload 原始鼠标事件。
	 */
	public override onPointerCancel(payload: RawPointerEventPayload): void {
		if (
			this.entitySelectionManager.renderer.sketchRotateEntitiesController?.handlePointerAngleDragCancel()
		) {
			this.consumePointerEvent(payload);
			return;
		}
		super.onPointerCancel(payload);
	}

	/**
	 * 抑制场景旋转拖动抬起后浏览器合成的单击事件。
	 *
	 * @param payload 原始鼠标事件。
	 */
	public override onClick(payload: RawPointerEventPayload): void {
		if (payload.button === 0 && this.suppressNextPointerAngleDragClick) {
			this.suppressNextPointerAngleDragClick = false;
			this.consumePointerEvent(payload);
			return;
		}
		super.onClick(payload);
	}

	
	protected resolveSelectionRole(): SketchEntitiesTransformSelectionRole {
		return this.entitySelectionManager.renderer.sketchRotateEntitiesController?.getSnapshot().activeSelectionRole
			?? SketchEntitiesTransformSelectionRole.Entities;
	}

	
	protected handleEntityPicked(object: THREE.Object3D): void {
		this.entitySelectionManager.renderer.sketchRotateEntitiesController?.handleEntityPick(object);
	}

	//------ 草图实体变换框选 开始------
	/**
	 * 处理旋转实体命令的框选结果。
	 *
	 * @param objects 框选命中的原始对象集合。
	 */
	protected handleEntitiesBoxSelected(objects: readonly THREE.Object3D[]): void {
		this.entitySelectionManager.renderer.sketchRotateEntitiesController?.handleEntityBoxSelection(objects);
	}
	//------ 草图实体变换框选 结束------

	
	protected handleReferenceCandidate(candidate: PointSelectionCandidate): void {
		this.entitySelectionManager.renderer.sketchRotateEntitiesController?.handleReferencePointSelection(candidate);
	}

	/**
	 * 消费已经由场景旋转拖动处理的原始鼠标事件。
	 *
	 * @param payload 原始鼠标事件。
	 */
	private consumePointerEvent(payload: RawPointerEventPayload): void {
		payload.nativeEvent.preventDefault();
		payload.nativeEvent.stopPropagation();
	}
}
